import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { Trophy, TrophyGroup } from '@plathunt/types';
import { useApi } from '../app/providers.js';
import type { GameHubContext } from './GameHub.js';

const typeLabel: Record<Trophy['psn']['type'], string> = {
  bronze: 'Bronze',
  silver: 'Silber',
  gold: 'Gold',
  platinum: 'Platin',
};

type Status = 'loading' | 'ready' | 'unavailable';

/**
 * Trophäen genau dieses Sets, gruppiert nach Base Game und DLC.
 * Earned State fehlt bewusst – der kommt aus dem PSN-Sync.
 */
export function TrophyList() {
  const { set } = useOutletContext<GameHubContext>();
  const api = useApi();

  const [groups, setGroups] = useState<TrophyGroup[]>([]);
  const [trophies, setTrophies] = useState<Trophy[]>([]);
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    Promise.all([api.trophies.listTrophyGroups(set.id), api.trophies.listTrophies(set.id)])
      .then(([loadedGroups, loadedTrophies]) => {
        if (cancelled) return;
        setGroups(loadedGroups);
        setTrophies(loadedTrophies);
        setStatus('ready');
      })
      .catch(() => {
        // Das Repository ist fuer diesen Pfad noch nicht implementiert.
        // Das ist kein Fehler des Nutzers und keine leere Liste.
        if (!cancelled) setStatus('unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [api, set.id]);

  if (status === 'loading') {
    return (
      <div aria-busy="true">
        <div className="ph-skeleton ph-skeleton--line" />
        <div className="ph-skeleton ph-skeleton--block" />
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div className="ph-card">
        <h2 className="ph-section__title">Trophäen noch nicht abrufbar</h2>
        <p className="ph-muted">
          Die Trophäenliste dieser Version ist serverseitig noch nicht angebunden. PlatHunt zeigt
          hier lieber nichts als erfundene Trophäen.
        </p>
      </div>
    );
  }

  if (trophies.length === 0) {
    return (
      <div className="ph-card">
        <h2 className="ph-section__title">Noch keine Trophäen importiert</h2>
        <p className="ph-muted">
          Das Set ist bekannt, die einzelnen Trophäen wurden aber noch nicht aus PSN übernommen.
        </p>
      </div>
    );
  }

  const ordered = [...groups].sort((a, b) => a.order - b.order);
  const ungrouped = trophies.filter((trophy) => !groups.some((g) => g.id === trophy.trophyGroupId));

  return (
    <>
      {ordered.map((group) => (
        <TrophyGroupCard
          key={group.id}
          group={group}
          trophies={trophies.filter((trophy) => trophy.trophyGroupId === group.id)}
        />
      ))}
      {ungrouped.length > 0 && <TrophyGroupCard group={null} trophies={ungrouped} />}
    </>
  );
}

function TrophyGroupCard({ group, trophies }: { group: TrophyGroup | null; trophies: Trophy[] }) {
  if (trophies.length === 0) return null;
  const isDlc = group?.kind === 'dlc';

  return (
    <div className="ph-card">
      <div className="ph-section__head">
        <h2 className={`ph-section__title${isDlc ? ' ph-section__title--psn' : ''}`}>
          {group?.name ?? 'Ohne Gruppe'}
        </h2>
        <span className="ph-badge">
          {isDlc ? 'DLC' : 'Base'} · {trophies.length}
        </span>
      </div>

      <ul className="ph-trophies">
        {[...trophies]
          .sort((a, b) => a.order - b.order)
          .map((trophy) => (
            <li key={trophy.id} className="ph-trophy">
              <span
                className={`ph-dot ph-dot--${trophy.psn.type}`}
                title={typeLabel[trophy.psn.type]}
                aria-hidden="true"
              />
              <div className="ph-trophy__text">
                <p className="ph-trophy__name">
                  {trophy.psn.hidden ? 'Versteckte Trophäe' : trophy.psn.name}
                </p>
                {!trophy.psn.hidden && trophy.psn.detail && (
                  <p className="ph-trophy__detail">{trophy.psn.detail}</p>
                )}
                {trophy.editorial.tip && <p className="ph-trophy__tip">{trophy.editorial.tip}</p>}
              </div>
              <span className="ph-trophy__flags">
                {trophy.editorial.missable && (
                  <span className="ph-flag ph-flag--missable">Missable</span>
                )}
                {trophy.editorial.pointOfNoReturn && (
                  <span className="ph-flag ph-flag--pnr">Point of no Return</span>
                )}
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
}
